import CancelIcon from '@mui/icons-material/Cancel';
import Chip, { ChipProps } from '@mui/material/Chip';
import { Fragment } from 'react';

type FilterChipOption<T> = {
  value: T;
  label: string;
  color: ChipProps['color'];
};

type FilterToggleChipsCommonProps<T> = {
  filterChipsOptions: Array<FilterChipOption<T>>;
  showAll?: boolean;
  disabled?: boolean;
  testId: string;
};

type FilterToggleChipsSingleProps<T> = {
  type: 'single';
  selectedChips: T | null;
  onChange: (newValue: T | null) => void;
} & FilterToggleChipsCommonProps<T>;

type FilterToggleChipsMultipleProps<T> = {
  type: 'multiple';
  selectedChips: Array<T>;
  onChange: (newValues: Array<T>) => void;
} & FilterToggleChipsCommonProps<T>;

export type FilterToggleChipsProps<T> =
  | FilterToggleChipsMultipleProps<T>
  | FilterToggleChipsSingleProps<T>;

export const FilterToggleChips = <T extends string | number>(props: FilterToggleChipsProps<T>) => {
  const {
    filterChipsOptions,
    selectedChips,
    onChange,
    disabled,
    testId,
    type,
    showAll = true,
  } = props;

  const isAllSelected = type === 'multiple' ? selectedChips.length === 0 : selectedChips === null;

  return (
    <Fragment>
      {showAll ? (
        <Chip
          data-testid="locations.filters.${testId}.buttons.all"
          label="All"
          color="grey"
          disabled={type === 'multiple' ? disabled || isAllSelected : false}
          onClick={() => {
            if (type === 'multiple') {
              onChange([]);
            } else {
              onChange(null);
            }
          }}
        />
      ) : null}
      {filterChipsOptions.map((p) => {
        const isSelected = (() => {
          if (type === 'multiple') {
            return isAllSelected || selectedChips.includes(p.value);
          } else {
            return p.value === selectedChips;
          }
        })();

        const variant: ChipProps['variant'] = (() => {
          if (type === 'multiple') {
            if (isSelected) {
              return 'filled';
            }
          } else if (type === 'single') {
            if (selectedChips === null || isSelected) {
              return 'filled';
            }
          }

          return 'outlined';
        })();

        return (
          <Chip
            data-testid={`locations.filters.${testId}.buttons.${p.label}`}
            key={p.value}
            label={p.label}
            sx={(theme) => ({
              '& .MuiChip-label': {
                color: isAllSelected || isSelected ? undefined : theme.palette.grey[600],
              },
            })}
            color={p.color}
            variant={variant}
            disabled={disabled}
            onClick={() => {
              if (type === 'single') {
                if (isSelected) {
                  onChange(null);
                } else {
                  onChange(p.value);
                }
              } else if (type === 'multiple') {
                if (!isSelected) {
                  const newValues = [...selectedChips, p.value];

                  if (newValues.length === filterChipsOptions.length) {
                    onChange([]);
                  } else {
                    onChange(newValues);
                  }
                }
              }
            }}
            deleteIcon={
              <CancelIcon data-testid={`locations.filters.${testId}.buttons.${p.label}.delete`} />
            }
            onDelete={
              isSelected
                ? () => {
                    if (type === 'multiple') {
                      const initialValues = isAllSelected
                        ? filterChipsOptions.map((pt) => pt.value)
                        : selectedChips;

                      const newValues = initialValues.filter((pt) => pt !== p.value);
                      onChange(newValues);
                    } else {
                      onChange(null);
                    }
                  }
                : undefined
            }
          />
        );
      })}
    </Fragment>
  );
};
